import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { UserRound, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';
import { translateError } from '../lib/translate-error';

type ContactLite = { id: string; name: string; phone: string };

/** Shows the contact a call is linked to, and lets staff (re)link or detach it
 *  by searching contacts. Uses the attachCallContact procedure. */
export function CallContactLink({ callId }: { callId: string }) {
  const { t } = useTranslation();
  const crud = useClientQueries(schema);
  const queryClient = useQueryClient();

  const q = crud.call.useFindUnique({
    where: { id: callId },
    select: { contact: { select: { id: true, name: true, phone: true } } },
  });
  const linked = (q.data as { contact?: ContactLite | null } | undefined)?.contact ?? null;

  const [term, setTerm] = useState('');
  const searchTerm = term.trim();
  const searchQ = crud.contact.useFindMany(
    {
      where: {
        OR: [
          { name: { contains: searchTerm, mode: 'insensitive' } },
          { phone: { contains: searchTerm } },
        ],
      },
      take: 6,
      orderBy: { name: 'asc' },
    },
    { enabled: searchTerm.length >= 2 },
  );
  const results = (searchQ.data as ContactLite[] | undefined) ?? [];

  const attach = crud.$procs.attachCallContact.useMutation();

  async function link(contactId: string | null) {
    try {
      await attach.mutateAsync({ args: { input: { callId, contactId } } });
      setTerm('');
      // Procedure mutations don't auto-invalidate model queries, so the old/new
      // contact's linked-call lists stay stale. Refresh every Call + Contact
      // query so both contacts reflect the change.
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = JSON.stringify(query.queryKey);
          return key.includes('Call') || key.includes('Contact');
        },
      });
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  return (
    <section className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <UserRound className="h-3.5 w-3.5" />
        {t('calls.contact')}
      </div>

      {linked ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/30 px-2.5 py-1.5 text-sm">
          <span className="min-w-0 truncate">
            <span className="font-medium">{linked.name}</span>
            <span className="ml-2 text-xs text-muted-foreground tabular-nums">{linked.phone}</span>
          </span>
          <button
            type="button"
            onClick={() => link(null)}
            disabled={attach.isPending}
            aria-label={t('calls.detach')}
            className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t('calls.noContactLinked')}</p>
      )}

      {/* Search-to-(re)link */}
      <input
        className="input"
        placeholder={t('calls.searchContact')}
        value={term}
        onChange={(e) => setTerm(e.target.value)}
      />
      {searchTerm.length >= 2 && results.length > 0 ? (
        <ul className="rounded-lg border border-border divide-y divide-border overflow-hidden">
          {results.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => link(c.id)}
                disabled={attach.isPending}
                className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-muted"
              >
                <span className="truncate">{c.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{c.phone}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
